import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserInfo, ChatPayload } from '../interfaces/chart-details.interface';
import { ChatStreamPostService } from './chat-stream-post.service';

export interface StreamData {
  type: 'text' | 'json';
  data: string | Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class ChatStreamService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private currentSubscription: Subscription | null = null;

  constructor(private chatStreamPost: ChatStreamPostService) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopCurrentStream();
  }

  /**
   * Start a new chat stream
   */
  startStream(
    chatPayload: ChatPayload,
    onNext: (data: StreamData) => void,
    onError: (error: Error) => void,
    onComplete: () => void,
  ): void {
    // Stop any existing stream first
    this.stopCurrentStream();

    this.currentSubscription = this.chatStreamPost
      .manualStreamChat(chatPayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: onNext,
        error: (error) => {
          this.stopCurrentStream(); // Clean up on error
          onError(error);
        },
        complete: () => {
          this.currentSubscription = null; // Clean up on completion
          onComplete();
        },
      });
  }

  /**
   * Stop the current stream
   */
  stopCurrentStream(): void {
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe();
      this.currentSubscription = null;
    }
  }

  /**
   * Check if a stream is currently active
   */
  isStreamActive(): boolean {
    return this.currentSubscription !== null;
  }

  /**
   * Generate user information payload
   */
  generateUserInfo(userAccount: {
    role?: string;
    industry?: string;
  }): UserInfo {
    return {
      user_role: this.formatRoleName(userAccount?.role),
      user_industry: userAccount?.industry
        ? userAccount.industry.toLowerCase()
        : null,
      user_interests: [],
    };
  }

  private formatRoleName(role: string): string {
    if (!role) {
      return null;
    }
    return role.toLowerCase().trim().replace(/\s+/g, '_');
  }
}
